import React, { useState, useRef } from 'react';
import { Upload, Trash2, Eye, EyeOff, Save, X, Camera, AlertCircle } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';

const ProfileEditor = ({ profileData, onSave, saving, currentUser }) => {
    const [formData, setFormData] = useState({
        name: profileData.name || '',
        age: profileData.age || '',
        sex: profileData.sex || '',
        sexualOrientation: profileData.sexualOrientation || '',
        city: profileData.city || '',
        income: profileData.income || '',
        photos: profileData.photos || [],
        visibility: profileData.visibility || {
            age: true,
            sex: true,
            sexualOrientation: true,
            city: true,
            income: false
        }
    });

    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const fileInputRef = useRef(null);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVisibilityToggle = (field) => {
        setFormData(prev => ({
            ...prev,
            visibility: {
                ...prev.visibility,
                [field]: !prev.visibility[field]
            }
        }));
    };

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files);

        if (formData.photos.length + files.length > 5) {
            alert('Poți încărca maximum 5 poze!');
            return;
        }

        setUploading(true);

        try {
            const uploadPromises = files.map(async (file, index) => {
                // Validare tip fișier
                if (!file.type.startsWith('image/')) {
                    throw new Error('Doar imagini sunt permise!');
                }

                // Validare dimensiune (max 5MB)
                if (file.size > 5 * 1024 * 1024) {
                    throw new Error('Imaginea este prea mare! Maxim 5MB.');
                }

                const timestamp = Date.now();
                const fileName = `${currentUser.uid}/${timestamp}_${index}.jpg`;
                const storageRef = ref(storage, `profile-photos/${fileName}`);

                // Upload
                setUploadProgress(prev => ({ ...prev, [index]: 0 }));
                await uploadBytes(storageRef, file);

                // Get URL
                const url = await getDownloadURL(storageRef);
                setUploadProgress(prev => ({ ...prev, [index]: 100 }));

                return url;
            });

            const urls = await Promise.all(uploadPromises);

            setFormData(prev => ({
                ...prev,
                photos: [...prev.photos, ...urls]
            }));

            setUploadProgress({});
        } catch (error) {
            console.error('Eroare la încărcarea pozelor:', error);
            alert(error.message || 'Eroare la încărcarea pozelor');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePhotoDelete = async (index) => {
        if (!window.confirm('Sigur vrei să ștergi această poză?')) {
            return;
        }

        try {
            const photoUrl = formData.photos[index];

            // Șterge din Storage
            try {
                const photoRef = ref(storage, photoUrl);
                await deleteObject(photoRef);
            } catch (error) {
                console.log('Nu s-a putut șterge din storage (posibil deja ștearsă)');
            }

            // Șterge din formData
            setFormData(prev => ({
                ...prev,
                photos: prev.photos.filter((_, i) => i !== index)
            }));
        } catch (error) {
            console.error('Eroare la ștergerea pozei:', error);
            alert('Eroare la ștergerea pozei');
        }
    };

    const handleSave = async () => {
        // Validări
        if (!formData.name.trim()) {
            alert('Te rugăm să introduci un nume!');
            return;
        }

        if (formData.age && (parseInt(formData.age) < 18 || parseInt(formData.age) > 100)) {
            alert('Vârsta trebuie să fie între 18 și 100 de ani!');
            return;
        }

        await onSave(formData);
        alert('Profil salvat cu succes!');
    };

    const renderVisibilityToggle = (field, label) => (
        <button
            onClick={() => handleVisibilityToggle(field)}
            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition ${formData.visibility[field]
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-600'
                }`}
        >
            {formData.visibility[field] ? (
                <>
                    <Eye size={16} />
                    Vizibil
                </>
            ) : (
                <>
                    <EyeOff size={16} />
                    Ascuns
                </>
            )}
        </button>
    );

    return (
        <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Editează Profil</h2>

                {/* Photo Upload Section */}
                <div className="mb-8 pb-8 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Poze Profil (Max 5)</h3>

                    {/* Photo Grid */}
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-4 mb-4">
                        {formData.photos.map((photo, index) => (
                            <div key={index} className="relative group">
                                <div className="aspect-square rounded-xl overflow-hidden border-2 border-slate-200">
                                    <img
                                        src={photo}
                                        alt={`Fotografie ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {index === 0 && (
                                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold">
                                        Profil
                                    </div>
                                )}
                                <button
                                    onClick={() => handlePhotoDelete(index)}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow-lg hover:bg-red-600"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}

                        {/* Upload Button */}
                        {formData.photos.length < 5 && (
                            <label className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 cursor-pointer flex flex-col items-center justify-center gap-2 transition bg-slate-50 hover:bg-blue-50">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePhotoUpload}
                                    className="hidden"
                                    disabled={uploading}
                                />
                                {uploading ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                ) : (
                                    <>
                                        <Camera size={24} className="text-slate-400" />
                                        <span className="text-xs text-slate-600 font-medium">Adaugă poză</span>
                                    </>
                                )}
                            </label>
                        )}
                    </div>

                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-900">
                            Prima poză va fi folosită ca poză de profil. Poți încărca max 5 poze (max 5MB fiecare).
                        </p>
                    </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Nume *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Introdu numele tău"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Age */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                                Vârstă
                            </label>
                            {renderVisibilityToggle('age', 'Vârstă')}
                        </div>
                        <input
                            type="number"
                            value={formData.age}
                            onChange={(e) => handleInputChange('age', e.target.value)}
                            placeholder="18-100"
                            min="18"
                            max="100"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Sex */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                                Sex
                            </label>
                            {renderVisibilityToggle('sex', 'Sex')}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => handleInputChange('sex', 'male')}
                                className={`py-3 px-4 rounded-lg border-2 font-medium transition ${formData.sex === 'male'
                                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                                        : 'border-slate-300 hover:border-slate-400'
                                    }`}
                            >
                                Bărbat
                            </button>
                            <button
                                onClick={() => handleInputChange('sex', 'female')}
                                className={`py-3 px-4 rounded-lg border-2 font-medium transition ${formData.sex === 'female'
                                        ? 'border-pink-600 bg-pink-50 text-pink-900'
                                        : 'border-slate-300 hover:border-slate-400'
                                    }`}
                            >
                                Femeie
                            </button>
                        </div>
                    </div>

                    {/* Sexual Orientation */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                                Orientare Sexuală
                            </label>
                            {renderVisibilityToggle('sexualOrientation', 'Orientare')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { value: 'heterosexual', label: 'Heterosexual' },
                                { value: 'gay', label: 'Gay' },
                                { value: 'lesbian', label: 'Lesbiană' },
                                { value: 'bisexual', label: 'Bisexual' }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => handleInputChange('sexualOrientation', option.value)}
                                    className={`py-3 px-4 rounded-lg border-2 font-medium transition text-sm ${formData.sexualOrientation === option.value
                                            ? 'border-purple-600 bg-purple-50 text-purple-900'
                                            : 'border-slate-300 hover:border-slate-400'
                                        }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* City */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                                Oraș
                            </label>
                            {renderVisibilityToggle('city', 'Oraș')}
                        </div>
                        <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            placeholder="ex: București, Cluj-Napoca"
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Income */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-slate-700">
                                Venituri Lunare (EUR)
                            </label>
                            {renderVisibilityToggle('income', 'Venituri')}
                        </div>
                        <select
                            value={formData.income}
                            onChange={(e) => handleInputChange('income', e.target.value)}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Selectează...</option>
                            <option value="0-2000">0 - 2,000 EUR</option>
                            <option value="2000-4000">2,000 - 4,000 EUR</option>
                            <option value="4000-6000">4,000 - 6,000 EUR</option>
                            <option value="6000+">6,000+ EUR</option>
                        </select>
                    </div>
                </div>

                {/* Save Button */}
                <div className="mt-8 flex gap-4">
                    <button
                        onClick={handleSave}
                        disabled={saving || uploading}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Se salvează...
                            </>
                        ) : (
                            <>
                                <Save size={20} />
                                Salvează Profil
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileEditor;